/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import Bowser from "bowser"
import { androidAppPackage, appStoreUrl, googlePlayUrl } from "const"
import { ActionButton, Card, HelpBand } from "components/help-page"

type MobilePlatform = "android" | "ios"

const stores: Record<MobilePlatform, { url: string; name: string }> = {
  android: { url: googlePlayUrl, name: "Google Play" },
  ios: { url: appStoreUrl, name: "the App Store" },
}

/** The phone or tablet platform of a browser, or null on a computer. */
const getMobilePlatform = ({
  userAgent,
  maxTouchPoints,
  platform,
}: Pick<Navigator, "userAgent" | "maxTouchPoints" | "platform">): MobilePlatform | null => {
  const osName = Bowser.getParser(userAgent).getOSName()
  // In Chrome's desktop mode (the default on large Android tablets) the user agent and the client
  // hints say Linux, but navigator.platform keeps Chrome's frozen Android value; a Linux desktop
  // reports "Linux x86_64".
  if (osName === "Android" || (osName === "Linux" && platform === "Linux armv81")) {
    return "android"
  }
  // An iPad requesting the desktop site (the Safari default since iPadOS 13) presents itself as a Mac;
  // the touch screen tells it apart from a real Mac.
  if (osName === "iOS" || (osName === "macOS" && maxTouchPoints > 1)) {
    return "ios"
  }
  return null
}

/**
 * An Android link that opens `url` in the Medaica Patient app, or the app's Google Play page when the app isn't
 * installed. Chrome follows it only from a tap, which is why the page offers it as a button rather than
 * redirecting.
 */
const androidAppLink = (url: URL): string =>
  `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=${androidAppPackage};` +
  `S.browser_fallback_url=${encodeURIComponent(googlePlayUrl)};end`

/**
 * Shown on /live-exam/<id> in a phone or tablet browser, where the exam can only run in the Medaica Patient app.
 * The link lands here when the phone opened it in a browser rather than in the app: from an email app's own
 * browser, or a link rewritten by the patient's mail service. On Android, "Open the Medaica app" opens this same
 * exam in the app. On iOS, Safari's Smart App Banner (public/index.html) offers "Open" instead; other iOS
 * browsers show no banner, so the page also points to the access code. Either way, without the app the patient
 * installs it from the store and comes back through the email.
 */
const MobileAppPage = ({ platform }: { platform: MobilePlatform }): ReactElement => {
  const store = stores[platform]

  return (
    <div className="max-w-md mx-auto pb-6 px-4">
      <div className="text-center pt-8 pb-6">
        <img src="/logo192.png" alt="" className="w-16 h-16 rounded-2xl mx-auto shadow" />
        <h1 className="text-2xl font-extrabold tracking-tight mt-4">Your exam runs in the Medaica Patient app</h1>
      </div>

      <Card title="Already have the app?" compact={true}>
        {platform === "android" ? (
          <ActionButton
            href={androidAppLink(new URL(window.location.href))}
            newWindow={false}
            variant="primary"
            title="Open the Medaica app"
            subtitle="Your exam opens in the app"
          />
        ) : (
          <p className="text-gray-700">
            Tap <strong>Open</strong> at the top of this page if you see it. Or open the Medaica app and enter the
            access code from your email.
          </p>
        )}
      </Card>

      <Card title="Don't have it yet?" compact={true}>
        <ActionButton
          href={store.url}
          newWindow={false}
          variant="secondary"
          title="Get the Medaica app"
          subtitle={`Free on ${store.name}`}
        />
        <p className="text-gray-600 mt-4">
          After installing, come back to your email and tap <strong>Join</strong> again, or open the app and enter your
          access code.
        </p>
      </Card>

      <HelpBand heading="Need help? Call us" />
    </div>
  )
}

export { getMobilePlatform, androidAppLink }
export type { MobilePlatform }
export default MobileAppPage
